import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Article } from "@/lib/types";
import { timeAgo } from "@/lib/api";
import { useCardTx, useLang, useArticlePath } from "@/lib/i18n";
import { SECTOR_LABELS } from "@/components/layernav/navConfig";
import { homeTrack } from "./shared";

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

/* Live freshness stamp — re-renders every 60s so readers feel the rotation */
const LiveAge = ({ iso }: { iso?: string }) => {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 60000);
    return () => clearInterval(id);
  }, []);
  return iso ? <>{timeAgo(iso)}</> : null;
};

const HeroFallbackArt = () => (
  <svg viewBox="0 0 640 400" preserveAspectRatio="xMidYMid slice" className="w-full h-full" aria-hidden
    data-testid="lead-fallback-art">
    <defs>
      <radialGradient id="lsf-g" cx="68%" cy="42%" r="78%">
        <stop offset="0%" stopColor="#7C3AED" stopOpacity="0.45" />
        <stop offset="55%" stopColor="#160b2e" />
        <stop offset="100%" stopColor="#0b0c10" />
      </radialGradient>
    </defs>
    <rect width="640" height="400" fill="url(#lsf-g)" />
    <g stroke="#00E5FF" strokeOpacity="0.3" strokeWidth="1" fill="none">
      <path d="M400 320 L490 235 L575 275 L625 165" />
      <path d="M370 175 L465 145 L545 85" />
      <path d="M490 235 L465 145" />
    </g>
    <g fill="#A78BFA">
      <circle cx="490" cy="235" r="4" fillOpacity="0.9" />
      <circle cx="575" cy="275" r="3" fillOpacity="0.7" />
      <circle cx="465" cy="145" r="3.5" fillOpacity="0.8" />
      <circle cx="545" cy="85" r="2.5" fillOpacity="0.6" />
      <circle cx="400" cy="320" r="3" fillOpacity="0.6" />
      <circle cx="625" cy="165" r="3" fillOpacity="0.7" />
    </g>
  </svg>
);

export const LeadStory = ({ article, top = false, eager = true, deferImage = false }:
  { article: Article; top?: boolean; eager?: boolean; deferImage?: boolean }) => {
  const { t } = useLang();
  const tx = useCardTx(article.slug);
  const toArticle = useArticlePath();
  const [errStep, setErrStep] = useState(0);
  const title = tx?.title || article.title;
  const summaryRaw = tx?.summary || article.summary_ai || article.summary || "";
  const tN = norm(title);
  const sN = norm(summaryRaw);
  const dek = summaryRaw && !sN.includes(tN.slice(0, 70)) && !tN.includes(sN.slice(0, 70)) ? summaryRaw : null;
  const cat = article.categories?.[0];
  const catLabel = cat ? (SECTOR_LABELS[cat] || (cat === "markets" ? "Markets" : cat)) : null;
  const primary = article.lead_visual?.url || article.hero_image_url || null;
  const src = errStep === 0 ? primary : errStep === 1 ? "/hero/markets.jpg" : null;

  return (
    <Link to={toArticle(article.slug)} data-testid="lead-story"
      onClick={() => homeTrack("lead_story", article.slug)}
      className="group relative block h-full rounded-xl overflow-hidden"
      style={{
        border: "1px solid transparent",
        background: "linear-gradient(#000000,#000000) padding-box, linear-gradient(90deg, #fd0017 0%, rgba(140,27,37,0) 57.2%) border-box",
      }}>
      <div aria-hidden className="absolute inset-x-0 top-0 h-56 sm:h-64 lg:inset-y-0 lg:left-auto lg:right-0 lg:h-full lg:w-[56%] pointer-events-none">
        {deferImage ? (
          <div className="lead-hero-blend w-full h-full bg-[#0e0f16]" />
        ) : src ? (
          <img src={src} alt="" fetchPriority={eager ? "high" : "low"}
            loading={eager ? undefined : "lazy"}
            onError={() => setErrStep((s) => (primary && primary !== "/hero/markets.jpg" ? s + 1 : 2))}
            className="lead-hero-blend w-full h-full object-cover" />
        ) : (
          <div className="lead-hero-blend w-full h-full"><HeroFallbackArt /></div>
        )}
        <div className="absolute inset-0 lg:hidden bg-gradient-to-t from-[#000000]/55 via-transparent to-[#000000]/20" />
        {/* Left→right #000000 vignette persisting to ~40% of the image (readability left, pop right).
            Bleeds 4px past the image's left edge to kill the subpixel seam line. */}
        <div className="hidden lg:block absolute inset-y-0 -left-1 right-0"
          style={{ background: "linear-gradient(to right, #000000 0%, #000000 2%, rgba(0,0,0,0.85) 17%, rgba(0,0,0,0.45) 30%, rgba(0,0,0,0) 43%)" }} />
      </div>
      <div aria-hidden className="absolute -bottom-24 left-[28%] h-56 w-80 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(124,58,237,0.12), transparent 70%)" }} />
      <div aria-hidden className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{ background: "radial-gradient(ellipse 60% 60% at 25% 15%, rgba(124,58,237,0.08), transparent 65%)" }} />

      <div className="relative z-10 flex flex-col p-5 sm:p-7 pt-48 sm:pt-56 lg:pt-6 lg:pb-6 lg:w-[60%] lg:min-h-[320px]">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] flex items-center gap-2">
          {article.is_breaking ? (
            <span data-testid="lead-breaking-label" className="flex items-center gap-1.5 text-[#fd0017]">
              <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-[#fd0017]" />
              {t("breaking")}
            </span>
          ) : catLabel ? (
            <span className="text-[#7a8299]">{catLabel}</span>
          ) : top ? (
            <span className="text-[#7a8299]">Top Story</span>
          ) : null}
          {article.published_at && (
            <span data-testid="lead-age-stamp" className="text-[#7a8299]">
              {(article.is_breaking || catLabel || top) ? "· " : ""}<LiveAge iso={article.published_at} />
            </span>
          )}
        </p>
        <h1 className="font-news font-black text-white text-[24px] sm:text-[28px] xl:text-[32px] leading-[1.14] tracking-tight mt-3 line-clamp-4">
          {title}
        </h1>
        {dek && (
          <p className="text-[#7a8299] mt-3 leading-relaxed line-clamp-2 text-sm sm:text-[15px] max-w-lg">
            {dek}
          </p>
        )}
        <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#7a8299] mt-auto pt-5">
          <LiveAge iso={article.published_at} />
          {article.read_minutes ? `  ·  ${article.read_minutes} ${t("minRead")}` : ""}
          {!article.is_breaking && catLabel ? "" : catLabel ? `  ·  ${catLabel}` : ""}
        </p>
        <span className="mt-3.5 inline-flex items-center gap-2 w-fit rounded-lg border border-[#7333e5] bg-[#010102] text-[#f2f5fc] font-semibold text-sm px-4 py-2 group-hover:border-[#9a5cff] group-hover:bg-[#7333e5]/[0.16] transition-colors">
          Read full story
          <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
};
